import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  input,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { BenchmarkDataChart, Ylabel } from 'gql/graphql';
import {
  Chart,
  Colors,
  LineController,
  PointElement,
  BarController,
  CategoryScale,
  LinearScale,
  BarElement,
  Legend,
  LineElement,
  ChartTypeRegistry,
  TimeScale,
  DoughnutController,
  ArcElement,
} from 'chart.js';
import { generateTonesAndShades, getColorHex } from '@utils/color';
import { CHART_COLORS, PALETTE } from '@constants/colors';
import { Tooltip } from 'chart.js';
import 'chartjs-adapter-date-fns';
import { TranslateService } from '@ngx-translate/core';
import { t } from '@utils/strings';
import ChartDataLabels from 'chartjs-plugin-datalabels';

// const barPosition = {
//   id: 'barPosition',
//   beforeDatasetsDraw: function (chart: Chart) {
//     const {
//       ctx,
//       data,
//       scales: { x },
//       chartArea: { right, left, bottom },
//     } = chart;
//     const meta = chart.getDatasetMeta(0);
//     const numberOfBars = meta.data.length;
//     const numberOfDatasets = data.datasets.length;
//     const chartWidth = right - left;
//     const barsGap = 20;
//     const barWidth =
//       (chartWidth - barsGap * (numberOfBars - 1)) /
//       (numberOfBars * numberOfDatasets); // MEMO: Calcolo fatto: cw = bw * bn * dn + bg * (bn - 1). Se non si vogliono spazi: cw / (bn * dn);

//     data.datasets.forEach((dataset, datasetIndex) => {
//       const meta = chart.getDatasetMeta(datasetIndex);
//       meta.data.forEach((bar, index) => {
//         bar.x =
//           left +
//           barWidth / 2 +
//           (index * numberOfDatasets + datasetIndex) * barWidth +
//           index * barsGap;
//         bar.getProps(['width'])['width'] = barWidth;

//         if (datasetIndex == 0) {
//           ctx.font = '500 12px ABBVoice, Roboto, "Helvetica Neue", sans-serif';
//           ctx.fillStyle = getColorHex('base-value-color-font');
//           ctx.textAlign = 'start';
//           if (data.labels && data.labels[index]) {
//             const { x: barX, width } = bar.getProps(['x', 'width']);
//             ctx.fillText(
//               data.labels[index] as string,
//               left + index * (numberOfDatasets * barWidth + barsGap),
//               bottom + 15
//             );
//           }
//         }
//       });
//     });
//   },
// };

const barPosition = (showLabels: boolean) => ({
  id: 'barPosition',
  beforeDatasetsDraw: function (chart: Chart) {
    const {
      ctx,
      data,
      chartArea: { bottom },
    } = chart;

    if (!showLabels) return;

    const metaArray = chart.data.datasets.map((_, idx) =>
      chart.getDatasetMeta(idx)
    );

    const labelCount = data.labels?.length ?? 0;

    for (let index = 0; index < labelCount; index++) {
      const positions: number[] = [];

      metaArray.forEach((meta) => {
        const bar = meta.data[index];
        if (bar) {
          const { x } = bar.getProps(['x']);
          positions.push(x);
        }
      });

      if (positions.length === 0) continue;

      // Calcolo della media (centro delle colonne affiancate)
      const averageX =
        positions.reduce((acc, val) => acc + val, 0) / positions.length;

      ctx.save();
      ctx.font = '500 12px ABBVoice, Roboto, "Helvetica Neue", sans-serif';
      ctx.fillStyle = getColorHex('base-value-color-font');
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';

      const label = data.labels?.[index] as string;
      ctx.fillText(label, averageX, bottom + 5);
      ctx.restore();
    }
  },
});

const getChartDatasetConfig = (
  chartType: keyof ChartTypeRegistry,
  n: number,
  idx: number
) => {
  const color =
    idx < CHART_COLORS.length
      ? CHART_COLORS[idx]
      : generateTonesAndShades(PALETTE.primary, n)[idx];

  if (chartType === 'bar') {
    return {
      borderRadius: 5,
      backgroundColor: color,
      maxBarThickness: 100, // Larghezza massima in pixel
      barPercentage: 0.9, // Percentuale dello spazio disponibile
      categoryPercentage: 0.8, // Percentuale dello spazio della categoria
    };
  } else {
    return {
      fill: false,
      borderColor: color,
      backgroundColor: color,
    };
  }
};

@Component({
  selector: 'abb-chart',
  standalone: true,
  imports: [CommonModule],
  host: {
    class: 'w-full h-full',
  },
  template: `
    <div
      class="relative w-full overflow-hidden"
      [ngClass]="{ 'h-full min-h-[200px]': grow(), 'h-[200px]': !grow() }"
    >
      <canvas #chartCanvas class="absolute top-0 left-0 w-full h-full"></canvas>
    </div>
  `,
  styles: ``,
})
export class AbbChartComponent implements AfterViewInit, OnChanges {
  @ViewChild('chartCanvas', { static: true }) chartCanvas!: ElementRef;
  prefix = inject(TRANSLATION_PREFIX);
  dataChart = input.required<
    | BenchmarkDataChart
    | {
        series: {
          name: string;
          values: { x: string | number; y: number }[] | number[];
        }[];
        labels?: Array<string>;
      }
  >();
  chartType = input.required<keyof ChartTypeRegistry>();
  yAxisLabel = input<Ylabel | null>();
  xAxisLabel = input<Ylabel | null>();
  grow = input(false);
  useCustomLabels = input(true);
  min = input<number>();
  max = input<number>();
  showColumnChartLabels = input(false);
  xAxisType = input<'category' | 'time'>('category');
  stepSize = input<number>();
  timeUnit = input<
    | 'millisecond'
    | 'second'
    | 'minute'
    | 'hour'
    | 'day'
    | 'week'
    | 'month'
    | 'quarter'
    | 'year'
    | undefined
  >();
  xAxisLabels = input<string[] | undefined>(undefined);

  private chart: Chart | null = null;
  constructor(private readonly translate: TranslateService) {
    Chart.register(
      Colors,
      Legend,
      LineController,
      BarController,
      LineElement,
      PointElement,
      BarElement,
      LinearScale,
      CategoryScale,
      Tooltip,
      TimeScale,
      DoughnutController,
      ArcElement
    );
    Chart.defaults.font.family =
      'ABBVoice, Roboto, "Helvetica Neue", sans-serif';
    Chart.defaults.color = getColorHex('base-value-color-font');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['dataChart'] && !changes['dataChart'].firstChange) ||
      (changes['min'] && !changes['min'].firstChange) ||
      (changes['max'] && !changes['max'].firstChange)
    ) {
      this.updateChart();
    }
  }

  ngAfterViewInit(): void {
    this.initChart();
  }

  private updateChart(): void {
    if (this.chart) {
      this.chart.destroy();
    }
    this.initChart();
  }

  private initChart(): void {
    const dataChart = this.dataChart();
    const timeUnit = this.timeUnit();
    const stepSize = this.stepSize();
    const xlabel = this.xAxisLabels();
    this.chart = new Chart(this.chartCanvas.nativeElement, {
      type: this.chartType(),
      data: {
        labels: (() => {
          if (xlabel) {
            return xlabel;
          }
          if ('xValues' in dataChart) {
            return dataChart.xValues;
          }
          return dataChart.labels;
        })(),
        datasets: (() => {
          if ('yValues' in dataChart) {
            return dataChart.yValues.map((v, idx) => ({
              ...v,
              ...getChartDatasetConfig(
                this.chartType(),
                dataChart.yValues.length,
                idx
              ),
            }));
          } else if (this.chartType() === 'doughnut') {
            return dataChart.series.map((s, idx) => ({
              data: s.values.map((v) =>
                typeof v !== 'number' ? v.x : v
              ) as any,
              label: t(this.prefix, s.name, this.translate),
              fill: true,
              backgroundColor: CHART_COLORS,
            }));
          }

          return dataChart.series.map((s, idx) => ({
            data: s.values as any,
            label: s.name,
            ...getChartDatasetConfig(
              this.chartType(),
              dataChart.series.length,
              idx
            ),
          }));
        })(),
      },
      options: {
        responsive: true,
        maintainAspectRatio: !this.grow(),
        layout: {
          padding: {
            top: this.chartType() === 'doughnut' ? 40 : 0,
            bottom: this.useCustomLabels()
              ? 20
              : this.chartType() === 'doughnut'
              ? 40
              : 0,
            left: this.chartType() === 'doughnut' ? 40 : 0,
            right: this.chartType() === 'doughnut' ? 40 : 0,
          },
        },
        scales:
          this.chartType() === 'line' || this.chartType() === 'bar'
            ? {
                y: {
                  min: this.min(),
                  max: this.max(),
                  display: true,
                  beginAtZero: true,
                  title: {
                    display:
                      this.yAxisLabel() && this.yAxisLabel()?.text
                        ? true
                        : false,
                    text: (() => {
                      if (this.yAxisLabel()?.text) {
                        let labelText = t(
                          'common',
                          this.yAxisLabel()!.text,
                          this.translate
                        );

                        if (
                          this.yAxisLabel()?.unit &&
                          this.yAxisLabel()?.unit.trim() !== ''
                        ) {
                          labelText = `${t('common', labelText, this.translate)} [${
                            this.yAxisLabel()?.unit
                          }]`;
                        }

                        return labelText;
                      }
                      return undefined;
                    })(),
                    font: {
                      size: 14,
                    },
                    color: getColorHex('base-value-color-font'),
                  },
                  ticks: {
                    padding: 10,
                    font: this.useCustomLabels()
                      ? {
                          weight: 400,
                          size: 12,
                          family:
                            'ABBVoice, Roboto, "Helvetica Neue", sans-serif',
                        }
                      : undefined,
                    color: this.useCustomLabels()
                      ? getColorHex('base-value-color-font')
                      : undefined,
                    callback: function (value) {
                      // Hide the 0 value
                      if (value === 0) {
                        return '';
                      }
                      return value;
                    },
                  },
                },
                x: {
                  labels: this.xAxisLabels(),
                     title: {
                    padding: {top:20, bottom:0},
                    display:
                      this.xAxisLabel() && this.xAxisLabel()?.text
                        ? true
                        : false,
                    text: (() => {
                      if (this.xAxisLabel()?.text) {
                        let labelText = t(
                          'common',
                          this.xAxisLabel()!.text,
                          this.translate
                        );

                        if (
                          this.xAxisLabel()?.unit &&
                          this.xAxisLabel()?.unit.trim() !== ''
                        ) {
                          labelText = `${labelText} [${
                            this.xAxisLabel()?.unit
                          }]`;
                        }

                        return labelText;
                      }
                      return undefined;
                    })(),
                    font: {
                      size: 14,
                    },
                    color: getColorHex('base-value-color-font'),
                  },
                  grid: {
                    display: false,
                  },
                  display: true,
                  ticks: {
                    display: this.chartType() !== 'bar',
                  },
                },
              }
            : {
                x: {
                  grid: {
                    display: false,
                  },
                  display: this.chartType() !== 'doughnut',
                  ticks: {
                    display:
                      !this.useCustomLabels() &&
                      this.chartType() !== 'doughnut',
                  },
                  type: this.xAxisType(),
                  time:
                    this.xAxisType() === 'time'
                      ? {
                          displayFormats: {
                            hour: 'HH:mm',
                          },
                          ...(timeUnit
                            ? {
                                unit: timeUnit,
                              }
                            : {}),
                          ...(stepSize
                            ? {
                                stepSize,
                              }
                            : {}),
                        }
                      : undefined,
                },
                y: {
                  min: this.min(),
                  max: this.max(),
                  display: this.chartType() !== 'doughnut',
                  beginAtZero: true,
                  border: {
                    display: false, // This hides the y-axis line
                  },
                  grid: {
                    display: this.chartType() !== 'doughnut',
                    drawOnChartArea: true, // Draw grid lines on the chart area
                    drawTicks: false, // Do not draw grid lines at tick marks
                  },
                  ticks: {
                    padding: 10,
                    font: this.useCustomLabels()
                      ? {
                          weight: 400,
                          size: 12,
                          family:
                            'ABBVoice, Roboto, "Helvetica Neue", sans-serif',
                        }
                      : undefined,
                    color: this.useCustomLabels()
                      ? getColorHex('base-value-color-font')
                      : undefined,
                    callback: function (value) {
                      // Hide the 0 value
                      if (value === 0) {
                        return '';
                      }
                      return value;
                    },
                  },
                },
              },
        plugins: {
          title: {
            display: true,
            text: 'Chart Title',
          },
          legend: {
            display: this.chartType() !== 'doughnut', // Nasconde la legenda per i grafici a ciambella
            labels: {
              boxWidth: 10,
              boxHeight: 10,
              useBorderRadius: true,
              borderRadius: 5,
              generateLabels: (chart) => {
                const originalLabels =
                  Chart.defaults.plugins.legend.labels.generateLabels(chart);
                return originalLabels.map((label) => ({
                  ...label,
                  text: t(this.prefix, label.text, this.translate), // Traduzione della legenda
                }));
              },
            },
          },
          ...(this.chartType() === 'doughnut' && {
            datalabels: {
              color: 'black',
              font: {
                size: 11, // Riduci leggermente la dimensione
                weight: 'bold',
              },
              align: 'end',
              anchor: 'end', // Aggiunto per un migliore controllo
              offset: 8, // Riduci l'offset
              clip: false, // Importante: permette alle label di uscire dal canvas
              clamp: false, // Non limita le label ai bordi del canvas
              borderColor: 'black',
              borderWidth: 1,
              backgroundColor: 'rgba(255, 255, 255, 0.9)', // Semi-trasparente
              borderRadius: 3,
              padding: 4,
              formatter: (value, context) => {
                const label = t(
                  this.prefix,
                  (context.chart.data.labels?.[context.dataIndex] as string) ??
                    'Unknown',
                  this.translate
                );
                const formattedValue = parseFloat(
                  Number(value).toFixed(2)
                ).toString();

                // Tronca le label troppo lunghe
                const maxLength = 20;
                const truncatedLabel =
                  label.length > maxLength
                    ? label.substring(0, maxLength) + '...'
                    : label;

                return `${truncatedLabel}: ${formattedValue}`;
              },
            },
          }),
        },
      },
      plugins: (() => {
        if (this.chartType() === 'bar') {
          return [barPosition(this.showColumnChartLabels())];
        } else if (this.chartType() === 'doughnut') {
          return [ChartDataLabels];
        } else {
          return [];
        }
      })(),
    });
  }
}
